import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";

const DEFAULT_URL = "https://prosecours.fr";

export default function ServicePage({ page }) {
  const url = `${DEFAULT_URL}/${page.slug}`;

  const ldService = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: page.h1,
    description: page.seo.description,
    url,
    provider: {
      "@type": "ProfessionalService",
      name: "ProSecours",
      url: DEFAULT_URL,
      telephone: "+33 7 44 88 33 81",
    },
    areaServed: { "@type": "Country", name: "France" },
  };
  const ldFaq = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: page.faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <div className="min-h-screen bg-[#f7f5f0] text-[#1a1918]" data-testid="service-page">
      <Helmet>
        <title>{page.seo.title}</title>
        <meta name="description" content={page.seo.description} />
        <meta name="keywords" content={page.seo.keywords} />
        <link rel="canonical" href={url} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={page.seo.title} />
        <meta property="og:description" content={page.seo.description} />
        <meta property="og:url" content={url} />
        <meta property="og:locale" content="fr_FR" />
        <script type="application/ld+json">{JSON.stringify(ldService)}</script>
        <script type="application/ld+json">{JSON.stringify(ldFaq)}</script>
      </Helmet>

      <header className="border-b border-[#1a1918]/15">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 font-serif-ed text-xl md:text-2xl">
            <img src="/logo-prosecours.png" alt="Logo ProSecours" className="h-8 w-auto" />
            <span>
              ProSecours
            </span>
          </Link>
          <Link to="/" className="overline text-[#1a1918]/70 hover:text-[#1a1918]">
            ← Retour à l'accueil
          </Link>
        </div>
      </header>

      <article className="max-w-[1440px] mx-auto px-6 md:px-10 py-16 md:py-24">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9 }}
          className="grid grid-cols-12 gap-4"
        >
          <div className="col-span-12 md:col-span-8 md:col-start-3">
            <span className="overline text-[#ce1e20]">{page.tag}</span>
            <h1 className="mt-6 font-serif-ed text-4xl md:text-6xl leading-[1.05] tracking-tight">
              {page.h1}
            </h1>
            <p className="mt-8 font-serif-ed italic text-xl md:text-2xl text-[#1a1918]/80 leading-relaxed">
              {page.lede}
            </p>
          </div>
        </motion.div>

        <div className="grid grid-cols-12 gap-4 mt-16 md:mt-24">
          <div className="col-span-12 md:col-span-8 md:col-start-3 space-y-12">
            {page.sections.map((s, i) => (
              <motion.section
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.8 }}
                className="border-t border-[#1a1918]/20 pt-8"
              >
                <h2 className="font-serif-ed text-3xl md:text-4xl italic leading-tight">
                  {s.h}
                </h2>
                <div className="mt-6 space-y-4 text-base md:text-lg leading-relaxed text-[#1a1918]/85">
                  {s.p.map((par, j) => (
                    <p key={j}>{par}</p>
                  ))}
                </div>
              </motion.section>
            ))}

            <motion.section
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8 }}
              className="border-t border-[#1a1918]/20 pt-8"
              data-testid="service-faq"
            >
              <h2 className="font-serif-ed text-3xl md:text-4xl italic leading-tight">
                Questions fréquentes.
              </h2>
              <div className="mt-6 divide-y divide-[#1a1918]/15">
                {page.faqs.map((f, i) => (
                  <div key={i} className="py-6" data-testid={`service-faq-${i}`}>
                    <h3 className="font-serif-ed text-xl md:text-2xl text-[#1a1918]">
                      {f.q}
                    </h3>
                    <p className="mt-3 text-base md:text-lg leading-relaxed text-[#1a1918]/80">
                      {f.a}
                    </p>
                  </div>
                ))}
              </div>
            </motion.section>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.9 }}
          className="grid grid-cols-12 gap-4 mt-20 md:mt-28"
        >
          <div className="col-span-12 md:col-span-8 md:col-start-3">
            <div className="bg-[#0a1e38] text-[#f7f5f0] p-8 md:p-14">
              <div className="overline text-[#f7f5f0]/60">Passer à l'action</div>
              <h3 className="mt-6 font-serif-ed text-3xl md:text-5xl leading-[1.05]">
                Vous choisissez la procédure.{" "}
                <span className="italic text-[#efece5]/95">Nous préparons le dossier</span>.
              </h3>
              <p className="mt-6 text-base md:text-lg text-[#f7f5f0]/80 max-w-xl leading-relaxed">
                Vous nous indiquez la formalité ou la procédure retenue. Nous
                rédigeons les actes, réunissons les pièces, demandons la date
                auprès du tribunal lorsque nécessaire et organisons le dépôt
                auprès du greffe compétent.
              </p>
              <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <Link
                  to="/#audit"
                  data-testid="service-cta-demarche"
                  className="inline-flex items-center gap-3 px-6 py-4 bg-[#ce1e20] text-white overline hover:bg-[#a71518] transition-colors"
                >
                  Décrire ma démarche →
                </Link>
                <a
                  href="tel:+33744883381"
                  data-testid="service-cta-phone"
                  className="ed-link overline text-[#f7f5f0]"
                >
                  ou appelez le 07 44 88 33 81
                </a>
              </div>
            </div>
          </div>
        </motion.div>
      </article>

      <footer className="border-t border-[#1a1918]/15 py-8">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 overline text-[#1a1918]/50">
          © ProSecours — Tous droits réservés
        </div>
      </footer>
    </div>
  );
}
